import { Download, Info, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ScheduleVerificationResponse, VerificationSummary } from '@/types/api';
import {
  ScheduleVerificationCompare,
  type ScheduleVerificationActions,
} from './ScheduleVerificationCompare';
import { cn } from '@/lib/utils';

interface ProgressInfo {
  total: number;
  resolved: number;
  verified: number;
  pending: number;
  etaLabel: string | null;
}

interface ScheduleVerificationSummaryProps {
  verification: ScheduleVerificationResponse | null;
  isLoading?: boolean;
  isVerifying: boolean;
  error?: string | null;
  /** Endpoint ausente / respuesta no JSON — no bloquea Programaciones */
  unavailable?: string | null;
  /** Tras Verificar: esperando uplink LoRa / contraste aún pendiente */
  awaitingDevice?: boolean;
  progress?: ProgressInfo | null;
  isLora?: boolean;
  onVerify: () => void | Promise<void>;
  onRefresh?: () => void | Promise<void>;
  actions?: Omit<ScheduleVerificationActions, 'onVerify'>;
  className?: string;
}

function emptySummary(): VerificationSummary {
  return {
    total: 0,
    verified: 0,
    confirmed: 0,
    sent: 0,
    pending: 0,
    mismatch: 0,
    missing: 0,
    error: 0,
  };
}

export function ScheduleVerificationSummary({
  verification,
  isLoading,
  isVerifying,
  error,
  unavailable,
  awaitingDevice = false,
  progress = null,
  isLora = true,
  onVerify,
  onRefresh,
  actions,
  className,
}: ScheduleVerificationSummaryProps) {
  const summary = verification?.summary ?? emptySummary();
  const issueCount = summary.mismatch + summary.missing + summary.error;
  const inProgressCount = summary.pending + summary.sent + summary.confirmed;
  const hasIssues = issueCount > 0;
  const isUnavailable = Boolean(unavailable) && !verification;
  const showProgress = awaitingDevice && inProgressCount > 0;
  const resolved = progress?.resolved ?? summary.verified + summary.mismatch + summary.missing + summary.error;
  const total = progress?.total || summary.total || 0;
  const pct = total > 0 ? Math.min(100, Math.round((resolved / total) * 100)) : 0;

  const compareActions: ScheduleVerificationActions | undefined = verification
    ? {
        ...actions,
        onVerify: async () => {
          await onVerify();
        },
      }
    : undefined;

  return (
    <Card
      className={cn(
        isUnavailable
          ? 'border-border'
          : hasIssues
            ? 'border-amber-500/40'
            : verification && summary.verified > 0 && issueCount === 0 && !showProgress
              ? 'border-green-500/30'
              : showProgress
                ? 'border-blue-500/30'
                : undefined,
        className
      )}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2 space-y-0">
        <div>
          <CardTitle className="text-sm font-medium">
            Estado de verificación
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Contraste plataforma vs dispositivo (independiente del envío/sync)
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {actions?.onImportFromDevice && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void actions.onImportFromDevice?.()}
              disabled={isVerifying || isLoading}
              title="Dispositivo → plataforma"
            >
              <Download className="h-4 w-4 mr-2" />
              Importar
            </Button>
          )}
          {onRefresh && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void onRefresh()}
              disabled={isLoading}
            >
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void onVerify()}
            disabled={isVerifying || isLoading || showProgress}
            title={
              showProgress
                ? 'Ya hay una verificación en cola; espere o vuelva más tarde'
                : undefined
            }
          >
            <RefreshCw
              className={cn('h-4 w-4 mr-2', (isVerifying || isLoading) && 'animate-spin')}
            />
            Verificar todo
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isUnavailable && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Verificación no disponible en este servidor. Las programaciones se pueden
              gestionar con normalidad. Cuando el backend v0.7 esté activo, use Verificar
              para contrastar plataforma y dispositivo.
            </AlertDescription>
          </Alert>
        )}

        {error && !isUnavailable && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!isUnavailable && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div>
                <p className="text-2xl font-semibold text-green-700">{summary.verified}</p>
                <p className="text-xs text-muted-foreground">Verificadas</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-amber-700">{summary.mismatch}</p>
                <p className="text-xs text-muted-foreground">No coinciden</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-red-600">{summary.missing}</p>
                <p className="text-xs text-muted-foreground">Faltan en dispositivo</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-muted-foreground">
                  {inProgressCount}
                </p>
                <p className="text-xs text-muted-foreground">En cola / proceso</p>
              </div>
            </div>

            {verification?.lastFullVerification && (
              <p className="text-xs text-muted-foreground text-center">
                Última verificación completa:{' '}
                {new Date(verification.lastFullVerification).toLocaleString('es-ES', {
                  timeZone: 'Europe/Madrid',
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}

            {showProgress && (
              <Alert className="border-blue-500/30 bg-blue-500/5">
                <RefreshCw className="h-4 w-4 animate-spin text-blue-700" />
                <AlertDescription className="space-y-2">
                  <p>
                    {isLora
                      ? `Verificación LoRa en curso: ${resolved}/${total || '?'} contrastadas.`
                      : `Verificación en curso: ${resolved}/${total || '?'}.`}
                    {progress?.etaLabel
                      ? ` ETA restante ~${progress.etaLabel} (1 slot por uplink).`
                      : ''}
                  </p>
                  <div className="h-2 rounded-full bg-blue-500/15 overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-xs">
                    Puede <strong>cerrar esta ventana</strong> y volver más tarde: el
                    servidor sigue contrastando. En el listado de controladoras le
                    avisaremos con un aviso cuando haya progreso.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {hasIssues && (
              <Alert className="border-amber-500/30 bg-amber-500/5">
                <AlertDescription>
                  Hay {issueCount} programación(es) con problemas respecto al dispositivo.
                  Revise el detalle y elija enviar (plataforma → dispositivo) o importar
                  (dispositivo → plataforma).
                </AlertDescription>
              </Alert>
            )}

            {!hasIssues && inProgressCount > 0 && !awaitingDevice && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Hay {inProgressCount} programación(es) sin contraste definitivo. Pulse{' '}
                  <strong>Verificar todo</strong> para pedir la lectura al dispositivo.
                  “En cola” no significa que falten en la controladora.
                </AlertDescription>
              </Alert>
            )}

            {verification && (
              <ScheduleVerificationCompare
                items={verification.schedules}
                actions={compareActions}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
